import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { TwoFactorStatus } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { session } from '@/shared/session/session';

import { RegenerateRecoveryCodesDialog } from '../components/regenerate-recovery-codes-dialog';
import { SetUpTwoFactorDialog } from '../components/set-up-two-factor-dialog';
import { TurnOffTwoFactorDialog } from '../components/turn-off-two-factor-dialog';
import { useTwoFactorStatus } from '../hooks/use-two-factor-status';
import { recoveryCodesLow } from '../schemas/two-factor-rules';

function TwoFactorDetails({ status, account }: { status: TwoFactorStatus; account: string }) {
  const { t } = useTranslation('auth');
  const format = useFormat();

  if (!status.enabled) {
    return (
      <>
        <CardContent className="flex flex-col gap-3 text-sm">
          <p className="text-muted-foreground">{t('security.offBody')}</p>
          {status.pendingSetup ? (
            <p className="text-muted-foreground" data-testid="two-factor-pending">
              {t('security.pending')}
            </p>
          ) : null}
        </CardContent>
        <CardFooter data-tour="platform.security.setup">
          <SetUpTwoFactorDialog account={account} resume={status.pendingSetup} />
        </CardFooter>
      </>
    );
  }

  const low = recoveryCodesLow(status.recoveryCodesLeft);
  return (
    <>
      <CardContent className="flex flex-col gap-4 text-sm">
        <dl
          className="grid gap-x-6 gap-y-2 sm:grid-cols-[max-content_1fr]"
          data-tour="platform.security.codes-left"
        >
          {status.enabledAt ? (
            <>
              <dt className="text-muted-foreground">{t('security.enabledAt')}</dt>
              <dd title={status.enabledAt}>{format.dateTime(status.enabledAt)}</dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">{t('security.codesLeftLabel')}</dt>
          <dd data-testid="recovery-codes-left">
            {t('security.codesLeft', { count: status.recoveryCodesLeft })}
          </dd>
        </dl>
        {low ? (
          <Alert variant="destructive" role="alert">
            <ShieldAlert aria-hidden />
            <AlertTitle>{t('security.lowTitle')}</AlertTitle>
            <AlertDescription>
              {t('security.low', { count: status.recoveryCodesLeft })}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <CardFooter className="flex flex-wrap gap-2">
        <RegenerateRecoveryCodesDialog account={account} />
        <TurnOffTwoFactorDialog />
      </CardFooter>
    </>
  );
}

function TwoFactorCard() {
  const { t } = useTranslation('auth');
  const { me } = session.useSession();
  const query = useTwoFactorStatus();
  const status = query.data;

  return (
    <Card className="max-w-2xl" data-tour="platform.security.status">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{t('security.twoFactorTitle')}</CardTitle>
          {status ? (
            <Badge
              variant={status.enabled ? 'default' : 'secondary'}
              data-testid="two-factor-status"
            >
              {status.enabled ? <ShieldCheck aria-hidden /> : null}
              {status.enabled ? t('security.on') : t('security.off')}
            </Badge>
          ) : null}
        </div>
        <CardDescription>{t('security.twoFactorDescription')}</CardDescription>
      </CardHeader>
      {query.isError ? (
        <CardContent>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
        </CardContent>
      ) : !status ? (
        <CardContent className="flex flex-col gap-2">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-9 w-40" />
        </CardContent>
      ) : (
        <TwoFactorDetails status={status} account={me?.user.email ?? ''} />
      )}
    </Card>
  );
}

/**
 * Explains why the rest of the console is closed while the platform requires two-factor and
 * this account has none; the route guard sends such a user here.
 */
function RequiredNotice() {
  const { t } = useTranslation('auth');
  const { me } = session.useSession();
  if (!me?.twoFactorRequired) return null;
  return (
    <Alert role="alert" className="mb-4 max-w-2xl" data-testid="two-factor-required">
      <ShieldAlert aria-hidden />
      <AlertTitle>{t('security.requiredTitle')}</AlertTitle>
      <AlertDescription>{t('security.required')}</AlertDescription>
    </Alert>
  );
}

/** Account security: two-factor authentication for the signed-in staff member. */
export function SecurityPage() {
  const { t } = useTranslation('auth');
  return (
    <PageContent>
      <PageHeader title={t('security.title')} description={t('security.description')} />
      <RequiredNotice />
      <FeatureErrorBoundary name="security">
        <TwoFactorCard />
      </FeatureErrorBoundary>
    </PageContent>
  );
}
